import { t } from 'i18next';
import { isNil } from 'lodash-es';
import * as yup from 'yup';

import { Account, AccountCommittedTruckCapacity } from '~hooks/useAccount/models';

const committedTruckCapacitySchema = yup.object().shape({
  id: yup.string().trim().notRequired(),
  equipmentType: yup
    .object()
    .shape({
      label: yup.string().trim().defined(),
      value: yup.string().trim().defined(),
    })
    .nullable()
    .test(
      'equipment-type-required',
      t('form_validation_errors.required_simple'),
      (equipmentType) => Boolean(equipmentType?.value),
    )
    .required(t('form_validation_errors.required_simple')),
  count: yup
    .number()
    .integer(
      t('form_validation_errors.should_be_integer', {
        field: t('account.form.truck_count'),
      }),
    )
    .min(
      1,
      t('form_validation_errors.minimum_field_is_num', {
        field: t('account.form.truck_count'),
        num: 1,
      }),
    )
    .typeError(
      t('form_validation_errors.should_be_number', {
        field: t('account.form.truck_count'),
      }),
    )
    .required(t('form_validation_errors.required_simple')),
});

export const truckCapacitySchema = yup.object().shape({
  truckCount: yup.lazy((value) => {
    const hasEmptyValue = isNil(value) || !String(value).trim().length;

    if (hasEmptyValue) {
      return yup.string().trim().notRequired();
    }

    return yup
      .number()
      .integer(
        t('form_validation_errors.should_be_integer', {
          field: t('account.form.truck_count'),
        }),
      )
      .min(
        0,
        t('form_validation_errors.minimum_field_is_num', {
          field: t('account.form.truck_count'),
          num: 0,
        }),
      )
      .typeError(
        t('form_validation_errors.should_be_number', {
          field: t('account.form.truck_count'),
        }),
      )
      .required();
  }),
  committedTruckCapacities: yup
    .array(committedTruckCapacitySchema)
    .required()
    .test(
      'unique-equipment-type',
      t('form_validation_errors.duplicate_truck_type'),
      (rows = []) => {
        const equipmentTypeIds = rows
          .map((row) => row.equipmentType?.value)
          .filter(Boolean);

        return equipmentTypeIds.length === new Set(equipmentTypeIds).size;
      },
    ),
  removedCommittedTruckCapacityIds: yup.array(yup.string().required()).required(),
});
export type TruckCapacityDto = yup.InferType<typeof truckCapacitySchema>;

// Rails applies nested attributes in array order, so a removed truck type has to be
// destroyed before the same type can be re-created in the same save. The payload index
// this produces is also what the API reports errors against, so the panel reads the same
// order back to anchor an indexed error to the row that submitted it.
export function orderCommittedTruckCapacityRows(data: TruckCapacityDto) {
  const rowIndexes = data.committedTruckCapacities.map((_, index) => index);
  const isPersisted = (index: number) => Boolean(data.committedTruckCapacities[index].id);

  return [
    ...data.removedCommittedTruckCapacityIds.map(() => undefined),
    ...rowIndexes.filter(isPersisted),
    ...rowIndexes.filter((index) => !isPersisted(index)),
  ];
}

export const emptyTruckCapacityRow = () => ({
  equipmentType: { label: '', value: '' },
  count: 1,
});

function toCommittedTruckCapacityRow(capacity: AccountCommittedTruckCapacity) {
  return {
    id: capacity.id,
    equipmentType: {
      label: capacity.equipmentType.name,
      value: capacity.equipmentType.id,
    },
    count: capacity.truckCount,
  };
}

export function setTruckCapacitySchemaDefaultValues(
  isConnectedAccount?: boolean,
  account?: Account,
) {
  const truckCount = isConnectedAccount
    ? account?.connectedAccount?.truckCount
    : account?.truckCount;
  const committedTruckCapacities = isConnectedAccount
    ? account?.connectedAccount?.committedTruckCapacities
    : account?.committedTruckCapacities;

  const defaultValues: TruckCapacityDto = {
    truckCount: truckCount ?? '',
    committedTruckCapacities: (committedTruckCapacities ?? []).map(
      toCommittedTruckCapacityRow,
    ),
    removedCommittedTruckCapacityIds: [],
  };

  return defaultValues;
}
